/**
 * Conversations Presenter Component (Refactored)
 * Lean presentational component focused on layout and composition
 *
 * SOLID Principles Applied:
 * - Single Responsibility: Focuses only on layout and composition
 * - Open/Closed: Extensible through composition of child components
 * - Dependency Inversion: Depends on abstractions (props/interfaces)
 */

import React, { useState } from 'react';
import { ChatInput } from '@/design-system';
import { ChatInputMobile } from '@/design-system/components/ChatInput/ChatInputMobile';
import { useFeedbackStore } from '@/store/feedbackStore';
import {
  backgroundColors,
  strokeColors,
  spacing,
  borders,
  typographyColors,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import {
  ModeChip,
  TabItem,
  PeopleAlsoAsk as PeopleAlsoAskType,
} from '@/constants/chatScreenData';
import { ChatMessage, ChatLocation } from '@/types/chatTypes';
import { ConversationResponsePair } from './response-pair';
import { LoadingResponsePlaceholder } from './loading/LoadingResponsePlaceholder';
import { ChatLoadingPlaceholder } from './ChatLoadingPlaceholder';
import { EnrichedExperiencesProvider } from './response-pair/EnrichedExperiencesProvider';
import { useConversationTabs } from './hooks/useConversationTabs';
import { useConversationScroll } from './hooks/useConversationScroll';
import { useResponsePairs } from './hooks/useResponsePairs';
import { getIconForChip } from './utils/iconHelpers';

export interface ConversationsPresenterProps {
  modeChips: ModeChip[];
  activeMode?: string;
  onModeChange?: (modeId: string) => void;
  promptValue?: string;
  onPromptChange?: (value: string) => void;
  onPromptSubmit?: () => void;
  tabs: TabItem[];
  activeTab?: string;
  onTabChange?: (tabId: string, conversationId?: string) => void;
  onMicClick?: () => void;
  onStopClick?: () => void;
  isRecording?: boolean;
  recordingTime?: number;
  overviewDescription: string;
  onCardClick?: (cardId: string) => void;
  onExperienceClick?: (experienceId: string) => void;
  messages: ChatMessage[];
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onCopy?: (messageId?: string, content?: string) => void;
  onShare?: (messageId?: string, content?: string) => void;
  onDownload?: (messageId?: string, content?: string) => void;
  peopleAlsoAsk: PeopleAlsoAskType[];
  onPeopleAlsoAskClick?: (itemId: string, question?: string) => void;
  onPlaceNameClick?: (placeName: string) => void;
  isLoading?: boolean;
  isLoadingResponse?: boolean;
  isLoadingFollowUps?: boolean;
  isLoadingExperiences?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onErrorDismiss?: () => void;
  chatInputRef?: React.RefObject<HTMLDivElement>;
  intent?: string;
  mood?: string;
  scrollToTopRef?: React.RefObject<{
    scrollToTop: () => void;
    scrollContainerToTop: () => void;
  }>;
}

export const ConversationsPresenter: React.FC<ConversationsPresenterProps> = ({
  modeChips,
  activeMode,
  onModeChange,
  promptValue,
  onPromptChange,
  onPromptSubmit,
  tabs,
  activeTab,
  onTabChange,
  onMicClick,
  onStopClick,
  isRecording,
  recordingTime,
  overviewDescription,
  onCardClick,
  onExperienceClick,
  messages,
  onLike,
  onDislike,
  onCopy,
  onShare,
  onDownload,
  onPeopleAlsoAskClick,
  onPlaceNameClick,
  isLoading,
  isLoadingResponse = false,
  isLoadingFollowUps = false,
  isLoadingExperiences = false,
  error,
  onRetry,
  onErrorDismiss,
  chatInputRef,
  intent,
  mood,
  scrollToTopRef,
}) => {
  const { getFeedback } = useFeedbackStore();

  // Mobile detection
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Custom hooks for state management
  const responsePairs = useResponsePairs(messages);
  const {
    lastResponsePairId,
    getActiveTabForConversation,
    handleConversationTabChange,
  } = useConversationTabs(tabs, activeTab, onTabChange, responsePairs);

  const {
    scrollableContainerRef,
    loadingPlaceholderRef,
    lastResponsePairRef,
    lastTabsSectionRef,
    lastHeaderRef,
    lastDividerAfterTabsRef,
    isWaitingForResponse,
    lastUserMessage,
  } = useConversationScroll(
    messages,
    isLoading,
    scrollToTopRef,
    isLoadingFollowUps
  );

  // Dynamic spacer height calculation to enable header scrolling to top
  // This calculates exactly how much space is needed below the last header
  // to allow it to reach the top of its container.
  const [dynamicSpacerHeight, setDynamicSpacerHeight] =
    React.useState<number>(0);

  React.useLayoutEffect(() => {
    const calculateSpacerHeight = () => {
      if (
        !scrollableContainerRef.current ||
        !lastHeaderRef.current ||
        !lastResponsePairRef.current ||
        responsePairs.length <= 1
      ) {
        setDynamicSpacerHeight(0);
        return;
      }

      // Check if we need a spacer based on the last response pair's active tab
      const lastPair = responsePairs[responsePairs.length - 1];
      const lastActiveTab = getActiveTabForConversation(
        lastPair.assistantMessage.id
      );
      const needsSpacer = ['places', 'map', 'experiences', 'images'].includes(
        lastActiveTab
      );

      if (!needsSpacer) {
        setDynamicSpacerHeight(0);
        return;
      }

      // Measure the visible height of the container
      const containerHeight = scrollableContainerRef.current.clientHeight;

      // Get the relative position of the header within the scrollable content
      const headerRect = lastHeaderRef.current.getBoundingClientRect();

      // Measure total content bottom after the header
      // We include everything up to the end of the last response or loading placeholder
      let contentBottom =
        lastResponsePairRef.current.getBoundingClientRect().bottom;

      if (loadingPlaceholderRef.current) {
        // If there's a loading placeholder, it increases the height below the header
        const loadingBottom =
          loadingPlaceholderRef.current.getBoundingClientRect().bottom;
        contentBottom = Math.max(contentBottom, loadingBottom);
      }

      // Distance from the top of the header to the end of all content
      const contentBelowHeader = contentBottom - headerRect.top;

      // To scroll the header to the top, the content below it must be at least as tall as the container
      const requiredSpacerHeight = Math.max(
        0,
        containerHeight - contentBelowHeader
      );

      // Only update if the change is significant to avoid unnecessary re-renders
      setDynamicSpacerHeight(prev =>
        Math.abs(prev - requiredSpacerHeight) > 1 ? requiredSpacerHeight : prev
      );
    };

    // Calculate immediately and also after a short delay for layout stability
    calculateSpacerHeight();
    const timeoutId = setTimeout(calculateSpacerHeight, 150);

    // Use ResizeObserver for more robust measurement (catches window resize and layout changes)
    const resizeObserver = new ResizeObserver(() => {
      calculateSpacerHeight();
    });

    if (scrollableContainerRef.current) {
      resizeObserver.observe(scrollableContainerRef.current);
    }

    // Also re-calculate if the last response pair's dimensions change
    if (lastResponsePairRef.current) {
      resizeObserver.observe(lastResponsePairRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [
    responsePairs,
    activeTab,
    isWaitingForResponse,
    getActiveTabForConversation,
    scrollableContainerRef,
    lastHeaderRef,
    lastResponsePairRef,
    loadingPlaceholderRef,
  ]);

  // Track which chat is currently visible in viewport
  const [visibleChatId, setVisibleChatId] = useState<string | null>(null);
  const visibleChatRef = React.useRef<string | null>(null);

  // Intersection Observer to detect which chat is visible; prefer discover tab so chat input stays when scrolling
  React.useEffect(() => {
    if (!scrollableContainerRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        const visibleThreshold = 0.15;
        const aboveThreshold = entries.filter(
          e => e.intersectionRatio > visibleThreshold
        );
        if (aboveThreshold.length === 0) return;

        // Prefer the visible entry whose chat has discover tab active (keeps chat box visible when scrolling discover)
        const withDiscover = aboveThreshold.find(entry => {
          const chatId = entry.target.getAttribute('data-chat-id');
          return chatId && getActiveTabForConversation(chatId) === 'discover';
        });
        const chosen =
          withDiscover ??
          aboveThreshold.reduce(
            (best, entry) =>
              entry.intersectionRatio > best.intersectionRatio ? entry : best,
            aboveThreshold[0]
          );

        if (!chosen) return;
        const chatId = chosen.target.getAttribute('data-chat-id');
        if (chatId && chatId !== visibleChatRef.current) {
          setVisibleChatId(chatId);
          visibleChatRef.current = chatId;
        }
      },
      {
        threshold: [0, 0.15, 0.25, 0.5, 0.75, 1],
        root: scrollableContainerRef.current,
        rootMargin: '-5% 0px -15% 0px',
      }
    );

    const responsePairElements =
      scrollableContainerRef.current.querySelectorAll('[data-chat-id]');
    responsePairElements.forEach(element => {
      observer.observe(element);
    });

    return () => observer.disconnect();
    // scrollableContainerRef omitted: refs are mutable and don't trigger re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsePairs, getActiveTabForConversation]);

  // Fallback: Check if no chat is visible, default to last chat
  React.useEffect(() => {
    if (!visibleChatId && responsePairs.length > 0) {
      const lastPair = responsePairs[responsePairs.length - 1];
      setVisibleChatId(lastPair.assistantMessage.id);
      visibleChatRef.current = lastPair.assistantMessage.id;
      console.log(
        '🔄 [VIEWPORT] Defaulting to last chat:',
        lastPair.assistantMessage.id
      );
    }
  }, [visibleChatId, responsePairs]);

  // Check if the currently visible chat has discover tab active
  const isDiscoverTabVisible = React.useMemo(() => {
    if (!visibleChatId) return responsePairs.length === 0; // Show for initial state

    const visiblePair = responsePairs.find(
      pair => pair.assistantMessage.id === visibleChatId
    );
    if (!visiblePair) return false;

    const activeTab = getActiveTabForConversation(visibleChatId);
    return activeTab === 'discover';
  }, [visibleChatId, responsePairs, getActiveTabForConversation]);

  // Check if the last assistant message is an error message to avoid duplicate display
  const lastAssistantMessage = messages
    .filter(m => m.role === 'assistant')
    .slice(-1)[0];
  const isLastMessageError =
    lastAssistantMessage?.content
      ?.trim()
      .startsWith('Sorry, I encountered an error') ?? false;

  // Only show error banner if error exists and it's not already shown as a chat message
  const shouldShowErrorBanner = error && !isLastMessageError;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        backgroundColor: backgroundColors.primary,
        borderRadius: borders.radius.lg,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
      }}
      className='w-full pt-[72px] md:pt-4 px-4 md:px-0 pb-4 md:pb-0'
    >
      {/* Error Message - Only show if not already displayed as chat message */}
      {shouldShowErrorBanner && (
        <div
          style={{
            backgroundColor: backgroundColors.primary,
            border: `1px solid ${strokeColors.base}`,
            borderRadius: borders.radius.md,
            padding: spacing[3],
            color: typographyColors.primary,
            fontSize: typography.size.sm,
            fontFamily: fontPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing[2],
            margin: '20px 0 8px 0',
          }}
        >
          <span style={{ flex: 1 }}>{error}</span>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}
          >
            {onRetry && (
              <button
                onClick={onRetry}
                style={{
                  background: 'none',
                  border: 'none',
                  color: typographyColors.primary,
                  cursor: 'pointer',
                  fontSize: typography.size.sm,
                  textDecoration: 'underline',
                  fontWeight: typography.weight.medium,
                }}
              >
                Retry
              </button>
            )}
            {onErrorDismiss && (
              <button
                onClick={onErrorDismiss}
                type='button'
                aria-label='Close'
                style={{
                  background: 'none',
                  border: 'none',
                  color: typographyColors.primary,
                  cursor: 'pointer',
                  padding: spacing[1],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: borders.radius.sm,
                }}
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  aria-hidden
                >
                  <path
                    d='M12 4L4 12M4 4l8 8'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scrollable Chat Responses Container */}
      <div
        ref={scrollableContainerRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[12],
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: spacing[2],
          paddingBottom: isMobile ? spacing[20] : spacing[2], // Extra padding for fixed chat input on mobile
          boxSizing: 'border-box',
          minHeight: 0,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          // iOS Safari fixes for smooth scrolling and preventing scroll issues
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          // Prevent negative scroll positions on iOS
          position: 'relative',
          // Ensure container doesn't shift on iOS
          top: 0,
          left: 0,
          right: 0,
        }}
        className='md:px-4 md:py-4 [&::-webkit-scrollbar]:hidden'
        onScroll={e => {
          // Prevent negative scroll position on iOS
          const target = e.currentTarget;
          if (target.scrollTop < 0) {
            // Use requestAnimationFrame to prevent layout thrashing
            requestAnimationFrame(() => {
              target.scrollTop = 0;
            });
          }
        }}
      >
        {/* Render each response pair */}
        {responsePairs.map((pair, index) => {
          const assistantMsg = pair.assistantMessage;
          const isLastPair = index === responsePairs.length - 1;
          const conversationId = assistantMsg.id;
          const activeTabForConversation =
            getActiveTabForConversation(conversationId);

          // Extract data for this specific response
          const responseLocations = extractLocationsForResponse(assistantMsg);
          // Determine which raw experiences to use for this response pair
          // CRITICAL: Only use experiences from THIS specific message, never fall back to container-level experiences
          // This ensures each chat conversation has isolated experiences
          const rawResponseExperiences =
            assistantMsg.experiences && assistantMsg.experiences.length > 0
              ? assistantMsg.experiences
              : null;
          const responseOverview = extractOverview(
            assistantMsg,
            overviewDescription
          );
          const responsePeopleAlsoAsk = extractPeopleAlsoAsk(
            assistantMsg,
            index
          );

          return (
            <div
              key={assistantMsg.id}
              ref={isLastPair ? lastResponsePairRef : undefined}
              data-chat-id={assistantMsg.id}
            >
              <EnrichedExperiencesProvider
                rawExperiences={rawResponseExperiences}
              >
                {enrichedResponseExperiences => (
                  <ConversationResponsePair
                    userMessage={pair.userMessage}
                    assistantMessage={assistantMsg}
                    isLastPair={isLastPair}
                    conversationId={conversationId}
                    modeChips={modeChips}
                    activeMode={activeMode}
                    onModeChange={onModeChange}
                    tabs={error ? [] : tabs}
                    activeTab={activeTabForConversation}
                    onTabChange={handleConversationTabChange}
                    intent={intent}
                    mood={mood}
                    locations={responseLocations}
                    onCardClick={onCardClick}
                    experiences={enrichedResponseExperiences}
                    onExperienceClick={onExperienceClick}
                    overviewDescription={responseOverview}
                    peopleAlsoAsk={responsePeopleAlsoAsk}
                    onPeopleAlsoAskClick={onPeopleAlsoAskClick}
                    onPlaceNameClick={onPlaceNameClick}
                    onLike={onLike}
                    onDislike={onDislike}
                    onCopy={onCopy}
                    onShare={onShare}
                    onDownload={onDownload}
                    isLoading={isLoading}
                    getFeedback={getFeedback}
                    getIcon={getIconForChip}
                    tabsSectionRef={isLastPair ? lastTabsSectionRef : undefined}
                    headerRef={isLastPair ? lastHeaderRef : undefined}
                    dividerAfterTabsRef={
                      isLastPair ? lastDividerAfterTabsRef : undefined
                    }
                  />
                )}
              </EnrichedExperiencesProvider>
            </div>
          );
        })}

        {/* Loading placeholder for new message - show whenever API is loading */}
        {isWaitingForResponse && (
          <div ref={loadingPlaceholderRef}>
            {lastUserMessage ? (
              <LoadingResponsePlaceholder
                lastUserMessage={lastUserMessage}
                modeChips={modeChips}
                activeMode={activeMode}
                onModeChange={onModeChange}
                tabs={error ? [] : tabs}
                activeTab={activeTab}
                lastResponsePairId={lastResponsePairId}
                getActiveTabForConversation={getActiveTabForConversation}
                onTabChange={onTabChange}
                handleConversationTabChange={handleConversationTabChange}
                getIcon={getIconForChip}
                isLoading={isLoading}
                isLoadingResponse={isLoadingResponse}
                isLoadingFollowUps={isLoadingFollowUps}
                isLoadingExperiences={isLoadingExperiences}
                messages={messages}
              />
            ) : (
              <ChatLoadingPlaceholder
                loadingPhase={
                  isLoadingResponse
                    ? 'thinking'
                    : isLoadingFollowUps
                      ? 'collecting'
                      : isLoadingExperiences
                        ? 'structuring'
                        : 'thinking'
                }
              />
            )}
          </div>
        )}

        {/* Spacer for non-discover tabs to enable header scrolling to top */}
        {(() => {
          // If the calculated height is 0 or we have only one pair, no spacer is needed
          if (responsePairs.length <= 1 || dynamicSpacerHeight <= 0)
            return null;

          const lastPair = responsePairs[responsePairs.length - 1];
          const lastConversationId = lastPair.assistantMessage.id;
          const lastActiveTab = getActiveTabForConversation(lastConversationId);

          // Only show spacer for specific tabs where the header needs to reach the top
          const needsSpacer = [
            'places',
            'map',
            'experiences',
            'images',
          ].includes(lastActiveTab);

          if (!needsSpacer) return null;

          return (
            <div
              style={{
                height: `${dynamicSpacerHeight}px`,
                flexShrink: 0,
              }}
              aria-hidden='true'
            />
          );
        })()}
      </div>

      {/* Fixed Chat Input - Only show on Discover tab that's currently visible */}
      {(() => {
        // Show chat input if no response pairs exist (to start conversation)
        // or if the currently visible chat has discover tab active
        const shouldShowChatInput =
          responsePairs.length === 0 || isDiscoverTabVisible;

        if (!shouldShowChatInput) {
          return null;
        }

        return (
          <div
            style={{
              background: `linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 40.385%, rgba(255, 255, 255, 1) 100%)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing[1],
              width: isMobile ? '90%' : '100%',
              boxSizing: 'border-box',
              flexShrink: 0,
              position: isMobile ? 'fixed' : 'sticky',
              bottom: 0,
              left: isMobile ? '50%' : 'auto', // Center horizontally on mobile
              transform: isMobile ? 'translateX(-50%)' : 'none', // Offset by half width
              zIndex: 10,
              marginTop: 'auto',
            }}
            className='px-2 md:px-4 pt-2 pb-0 md:pt-4 md:pb-4'
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[1],
                alignItems: 'stretch',
                justifyContent: 'center',
              }}
            >
              {isMobile ? (
                <ChatInputMobile
                  ref={chatInputRef}
                  value={promptValue}
                  onChange={onPromptChange}
                  onSubmit={onPromptSubmit}
                  placeholder='Ask anything about travel.'
                  isLoading={isLoading}
                  disabled={isLoading}
                  onMicClick={onMicClick}
                  onStopClick={onStopClick}
                  isRecording={isRecording}
                  recordingTime={recordingTime}
                  inputFontSize={typography.size.base}
                  showMicrophone={true}
                  showAttachment={false}
                />
              ) : (
                <ChatInput
                  ref={chatInputRef}
                  value={promptValue}
                  onChange={onPromptChange}
                  onSubmit={onPromptSubmit}
                  placeholder='Ask anything about travel.'
                  isLoading={isLoading}
                  disabled={isLoading}
                  onMicClick={onMicClick}
                  onStopClick={onStopClick}
                  isRecording={isRecording}
                  recordingTime={recordingTime}
                  inputFontSize={typography.size.base}
                />
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Helper functions for data extraction (following Single Responsibility Principle)

function extractLocationsForResponse(
  assistantMsg: ChatMessage
): ChatLocation[] {
  // CRITICAL: Only use locations from THIS specific message, never fall back to container-level locations
  // This ensures each chat conversation has isolated locations
  // Just return raw locations from the message, let components handle filtering and mapping
  return assistantMsg.locations || [];
}

function extractOverview(assistantMsg: ChatMessage, fallback: string): string {
  return (
    assistantMsg.overview ||
    assistantMsg.content.substring(0, 300) +
      (assistantMsg.content.length > 300 ? '...' : '') ||
    fallback
  );
}

function extractPeopleAlsoAsk(
  assistantMsg: ChatMessage,
  index: number
): PeopleAlsoAskType[] {
  // Only return questions if API provides them - don't use dummy/fallback questions
  if (
    !assistantMsg.followUpQuestions ||
    assistantMsg.followUpQuestions.length === 0
  ) {
    return [];
  }

  return assistantMsg.followUpQuestions.map((question, qIndex) => ({
    id: `response_${index}_followup_${qIndex}`,
    question,
  }));
}
